import React from "react";
import {
  AlertTriangle, ArrowDownLeft, ArrowUpRight, Ban, CheckCircle2, Circle, Clock, Crown, Info, XCircle,
} from "lucide-react";
import { cx, formatINR, formatINRCompact } from "./format";

const TONES = {
  success: { cls: "bg-cm-success-soft text-cm-success", icon: CheckCircle2 },
  warning: { cls: "bg-cm-warning-soft text-cm-warning", icon: Clock },
  danger: { cls: "bg-cm-danger-soft text-cm-danger", icon: XCircle },
  info: { cls: "bg-cm-info-soft text-cm-info", icon: Info },
  neutral: { cls: "bg-cm-surface-3 text-cm-muted", icon: Circle },
  gold: { cls: "bg-cm-gold-soft text-cm-gold-text", icon: Crown },
};

// Common statuses across the app → tone + icon, so the same word always looks the same.
const STATUS_MAP = [
  [/^(active|approved|verified|success|successful|completed|complete|paid|settled|open|online|present|checked[ -]?in|seated|confirmed|enabled|yes|credit|deposit|in)$/i, "success"],
  [/^(pending|processing|in[ -]?progress|waiting|on[ -]?hold|partial|review|under review|kyc pending|draft|scheduled|queued|break|on break)$/i, "warning"],
  [/^(inactive|rejected|failed|failure|declined|banned|blocked|suspended|cancelled|canceled|closed|expired|disabled|deleted|no|debit|cash[ -]?out|cashout|withdrawal|out|error|absent)$/i, "danger"],
  [/^(info|new|booked|reserved|buy[ -]?in|buyin)$/i, "info"],
  [/^(vip|premium|gold)$/i, "gold"],
];

const ICON_OVERRIDES = {
  banned: Ban, blocked: Ban, suspended: Ban,
  deposit: ArrowDownLeft, credit: ArrowDownLeft, in: ArrowDownLeft,
  "cash-out": ArrowUpRight, cashout: ArrowUpRight, "cash out": ArrowUpRight, withdrawal: ArrowUpRight, debit: ArrowUpRight, out: ArrowUpRight,
  error: AlertTriangle, failed: AlertTriangle,
};

/**
 * Static class strings per tone (Tailwind can't see classes built at runtime).
 *   soft: tinted background + matching text   icon: same, for icon chips   text: text colour only
 */
export const TONE_CLASSES = {
  success: { soft: "bg-cm-success-soft text-cm-success", text: "text-cm-success" },
  warning: { soft: "bg-cm-warning-soft text-cm-warning", text: "text-cm-warning" },
  danger: { soft: "bg-cm-danger-soft text-cm-danger", text: "text-cm-danger" },
  info: { soft: "bg-cm-info-soft text-cm-info", text: "text-cm-info" },
  gold: { soft: "bg-cm-gold-soft text-cm-gold-text", text: "text-cm-gold-text" },
  red: { soft: "bg-cm-red-soft text-cm-red", text: "text-cm-red" },
  neutral: { soft: "bg-cm-surface-3 text-cm-muted", text: "text-cm-muted" },
};
export const toneSoft = (tone) => (TONE_CLASSES[tone] || TONE_CLASSES.neutral).soft;

/** Square icon chip in a tone colour (list rows, alerts, KPI accents). */
export const IconChip = ({ icon: Icon, tone = "gold", size = 36, className }) => (
  <span
    className={cx("rounded-cm grid place-items-center shrink-0", toneSoft(tone), className)}
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    <Icon size={Math.round(size * 0.45)} />
  </span>
);

export const toneForStatus = (status) => {
  const s = String(status ?? "").trim();
  for (const [re, tone] of STATUS_MAP) if (re.test(s)) return tone;
  return "neutral";
};

/**
 * Status is always pill + icon + word (never colour alone).
 *   <StatusPill status="Pending" />            tone picked from the word
 *   <StatusPill tone="gold" icon={Crown}>VIP</StatusPill>
 */
export const StatusPill = ({ status, tone, icon, children, size = "md", className }) => {
  const label = children ?? status ?? "—";
  const t = tone || toneForStatus(status ?? (typeof children === "string" ? children : ""));
  const conf = TONES[t] || TONES.neutral;
  const Icon = icon === null ? null : icon || ICON_OVERRIDES[String(status ?? label).toLowerCase()] || conf.icon;
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap align-middle",
        size === "sm" ? "h-5 px-2 text-[11px]" : "h-6 pl-2 pr-2.5 text-xs",
        conf.cls,
        className,
      )}
    >
      {Icon && <Icon size={size === "sm" ? 11 : 13} aria-hidden="true" />}
      {label}
    </span>
  );
};

/** Plain neutral tag (counts, categories) without status meaning. */
export const Badge = ({ children, tone = "neutral", className }) => (
  <span
    className={cx(
      "inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold whitespace-nowrap",
      (TONES[tone] || TONES.neutral).cls,
      className,
    )}
  >
    {children}
  </span>
);

/** Money that reads the same everywhere: + teal for in, − red for out. */
export const Money = ({ value, sign = false, compact = false, decimals = 0, className }) => {
  const n = Number(value);
  const tone = !Number.isFinite(n) || !sign || n === 0 ? "" : n > 0 ? "text-cm-success" : "text-cm-danger";
  const text = compact ? formatINRCompact(n) : formatINR(n, { sign, decimals });
  return <span className={cx("tabular-nums whitespace-nowrap", tone, className)}>{text}</span>;
};
